import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { AdminLeagueRow } from '@/components/AdminLeagueRow';
import { LeagueDisableConfirm } from '@/components/LeagueDisableConfirm';
import { CountryLeaguesGroup } from '@/components/CountryLeaguesGroup';
import { CircleIconButton } from '@/components/ui/CircleIconButton';
import CustomSelect, { type SelectOption } from '@/components/ui/CustomSelect';
import { SearchField } from '@/components/ui/SearchField';
import { useCountryExpansion } from '@/hooks/useCountryExpansion';
import { useCountryGroups } from '@/hooks/useCountryGroups';
import { useLeagueEnabledToggle } from '@/hooks/useLeagueEnabledToggle';
import { LEAGUE_SYNC_MODES, type LeagueSyncMode } from '@/constants';
import type {
  AdminLeague,
  AdminLeagueFilterOptions,
  AdminLeaguesQuery,
  AdminLeagueSyncResult,
} from '@/types/league';
import styles from './LeaguesTab.module.scss';

type EnabledFilter = 'all' | 'enabled' | 'disabled';

const SYNC_MODE_LABEL_KEYS: Record<LeagueSyncMode, string> = {
  [LEAGUE_SYNC_MODES.FULL_ACTIVE]: 'admin:syncModeFullActive',
  [LEAGUE_SYNC_MODES.PENDING]: 'admin:syncModePending',
  [LEAGUE_SYNC_MODES.RECENTLY_CLOSED]: 'admin:syncModeRecentlyClosed',
  [LEAGUE_SYNC_MODES.FULL]: 'admin:syncModeFull',
  [LEAGUE_SYNC_MODES.FULL_LAST_3_YEARS]: 'admin:syncModeFullLast3Years',
  [LEAGUE_SYNC_MODES.DEFAULT]: 'admin:syncModeDefault',
};

const ALL = 'all';
/** Countries the admin expanded, kept for the browser tab. */
const EXPANDED_COUNTRIES_STORAGE_KEY = 'admin-leagues:expanded-countries';
const NO_LEAGUES: AdminLeague[] = [];

/**
 * `/admin/leagues` — every league grouped into collapsible country cards
 * (collapsed by default; collapsed bodies are not rendered, the list holds
 * well over a thousand leagues). Country / season / enabled filters query the
 * server; the text search filters the loaded list on the client.
 */
const LeaguesTab: React.FC = () => {
  const { t } = useTranslation(['admin', 'league', 'common']);
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>(ALL);
  const [season, setSeason] = useState(ALL);
  const [country, setCountry] = useState(ALL);
  const [syncMode, setSyncMode] = useState<LeagueSyncMode>(LEAGUE_SYNC_MODES.FULL_ACTIVE);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const activeQuery = search.trim();

  const { data: filterOptions } = useQuery<AdminLeagueFilterOptions>({
    queryKey: ['admin-league-filter-options'],
    queryFn: async () => (await apiClient.getAdminLeagueFilterOptions()).data.data,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery<AdminLeague[]>({
    queryKey: ['admin-leagues', enabledFilter, season, country],
    queryFn: async () => {
      const params: AdminLeaguesQuery = {};
      if (enabledFilter !== ALL) params.isEnabled = String(enabledFilter === 'enabled');
      if (season !== ALL) params.season = season;
      if (country !== ALL) params.country = country;
      const response = await apiClient.getAdminLeagues(params as Record<string, string>);
      return response.data.data.leagues || [];
    },
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const leagues = data ?? NO_LEAGUES;
  const { visibleGroups } = useCountryGroups(leagues, activeQuery);
  const expansion = useCountryExpansion(EXPANDED_COUNTRIES_STORAGE_KEY, activeQuery);

  const showError = useCallback((text: string) => setMessage({ type: 'error', text }), []);
  const { togglingLeagueId, requestToggle, pendingDisable, confirmDisable, cancelDisable } =
    useLeagueEnabledToggle(showError);

  const syncMutation = useMutation({
    mutationFn: async (mode: LeagueSyncMode) => {
      const response = await apiClient.syncLeaguesFromApi(mode);
      return response.data.data as AdminLeagueSyncResult;
    },
    onSuccess: (result) => {
      setMessage({
        type: 'success',
        text: t('admin:syncLeaguesSuccess', {
          synced: result.synced,
          created: result.created,
          updated: result.updated,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin-leagues'] });
      queryClient.invalidateQueries({ queryKey: ['admin-league-filter-options'] });
      queryClient.invalidateQueries({ queryKey: ['admin-competitions'] });
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.response?.data?.error || t('admin:syncLeaguesError') });
    },
  });

  const seasonOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL, label: t('admin:filterSeasonAll') },
      ...(filterOptions?.seasons ?? []).map((s) => ({ value: s, label: s })),
    ],
    [filterOptions, t],
  );

  const countryOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL, label: t('admin:filterCountryAll') },
      ...(filterOptions?.countries ?? []).map((c) => ({ value: c, label: c })),
    ],
    [filterOptions, t],
  );

  const syncModeOptions = useMemo<SelectOption[]>(
    () =>
      Object.values(LEAGUE_SYNC_MODES).map((mode) => ({
        value: mode,
        label: t(SYNC_MODE_LABEL_KEYS[mode]),
      })),
    [t],
  );

  const findOption = (options: SelectOption[], value: string) =>
    options.find((o) => o.value === value) ?? null;

  const enabledCount = leagues.filter((l) => l.isEnabled).length;
  const hasActiveFilters = Boolean(activeQuery) || enabledFilter !== ALL || season !== ALL || country !== ALL;
  const visibleKeys = visibleGroups.map((g) => g.key);
  const allCollapsed = visibleKeys.every((key) => !expansion.isExpanded(key));

  const resetFilters = () => {
    setSearch('');
    setEnabledFilter(ALL);
    setSeason(ALL);
    setCountry(ALL);
  };

  const headerRight = (
    <div className={styles.syncControls}>
      <div className={styles.syncModeWrapper}>
        <CustomSelect
          id="league-sync-mode"
          ariaLabel={t('admin:syncMode')}
          options={syncModeOptions}
          value={findOption(syncModeOptions, syncMode)}
          onChange={(option) => option && setSyncMode(option.value as LeagueSyncMode)}
          variant="basic"
          isSearchable={false}
          menuPortal
          placeholder={t('admin:syncMode')}
        />
      </div>
      <button
        type="button"
        className="ls-btn"
        onClick={() => syncMutation.mutate(syncMode)}
        disabled={syncMutation.isPending}
        aria-busy={syncMutation.isPending}
      >
        {syncMutation.isPending ? (
          <span className={styles.syncButtonInner}>
            <span className="spinner spinner--sm" />
            {t('admin:syncing')}
          </span>
        ) : (
          t('admin:syncLeagues')
        )}
      </button>
    </div>
  );

  return (
    <MainSection title={t('admin:leagues')} headerRight={headerRight}>
      <div className={styles.wrapper}>
        {message && (
          <div className={`${styles.message} ${message.type === 'success' ? styles.success : styles.error}`}>
            {message.text}
            <button type="button" onClick={() => setMessage(null)} aria-label={t('common:close')}>
              x
            </button>
          </div>
        )}

        <div className={styles.toolbar}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder={t('admin:searchLeagues')}
            className={styles.search}
            testId="admin-leagues-search"
          />
          <div className={styles.filterSelectWrapper}>
            <CustomSelect
              id="league-country"
              ariaLabel={t('admin:filterCountry')}
              options={countryOptions}
              value={findOption(countryOptions, country)}
              onChange={(option) => setCountry(option?.value ?? ALL)}
              variant="basic"
              isSearchable
              menuPortal
              placeholder={t('admin:filterCountry')}
            />
          </div>
          <div className={styles.filterSelectWrapper}>
            <CustomSelect
              id="league-season"
              ariaLabel={t('admin:filterSeason')}
              options={seasonOptions}
              value={findOption(seasonOptions, season)}
              onChange={(option) => setSeason(option?.value ?? ALL)}
              variant="basic"
              isSearchable={false}
              menuPortal
              placeholder={t('admin:filterSeason')}
            />
          </div>
        </div>

        <div className={styles.filterRow}>
          {(['all', 'enabled', 'disabled'] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={enabledFilter === f}
              className={`${styles.filterTab} ${enabledFilter === f ? styles.active : ''}`}
              onClick={() => setEnabledFilter(f)}
            >
              {f === ALL
                ? t('admin:filterAll')
                : f === 'enabled'
                  ? t('admin:filterEnabled')
                  : t('admin:filterDisabled')}
            </button>
          ))}
          <span className={styles.toggleAll}>
            <CircleIconButton
              icon={allCollapsed ? 'chevrons-down' : 'chevrons-up'}
              ariaLabel={allCollapsed ? t('league:expandAll') : t('league:collapseAll')}
              onClick={() => expansion.setAll(visibleKeys, allCollapsed)}
              shape="square"
              disabled={visibleKeys.length === 0}
              ariaExpanded={visibleKeys.length === 0 ? undefined : !allCollapsed}
              testId="admin-leagues-toggle-all"
            />
          </span>
        </div>

        <p className={styles.stats}>
          {t('admin:leaguesStats', { total: leagues.length, enabled: enabledCount })}
        </p>

        {isLoading ? null : visibleGroups.length === 0 ? (
          <div className={styles.emptyState} data-testid="admin-leagues-empty">
            <span>{t('admin:noLeaguesFound')}</span>
            {hasActiveFilters && (
              <button type="button" className="ls-btn" onClick={resetFilters}>
                {t('admin:resetFilters')}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.groups}>
            {visibleGroups.map((group) => (
              <CountryLeaguesGroup
                key={group.key}
                group={group}
                lazyBody
                expanded={expansion.isExpanded(group.key)}
                onToggle={() => expansion.toggle(group.key)}
                renderRow={(league) => (
                  <AdminLeagueRow
                    league={league}
                    busy={togglingLeagueId === league.id}
                    onToggleEnabled={() => requestToggle(league)}
                  />
                )}
              />
            ))}
          </div>
        )}
      </div>
      <LeagueDisableConfirm impact={pendingDisable} onConfirm={confirmDisable} onCancel={cancelDisable} />
    </MainSection>
  );
};

export default LeaguesTab;
